import React, { useEffect } from 'react';
import UnifiedNavigation from '../../components/Navigation/UnifiedNavigation';
import Footer from '../../components/Footer/Footer';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import { useScrollManager } from '../../hooks/useScrollManager';
import { useOverlayManager } from '../../contexts/OverlayContext';
import { initializeAllFeatures } from '../../utils/interactiveFeatures';
import { useTranslation } from 'react-i18next';
import './ExperiencesIguazu.css';

const ExperiencesIguazu: React.FC = () => {
  const { scrollState } = useScrollManager();
  const { setHoveredDestination } = useOverlayManager();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    initializeAllFeatures();
    // Clear any lingering hover state to ensure correct footer variant
    setHoveredDestination(null);
  }, []);

  // Experience items data with translated content
  const experiences = [
    {
      id: 1,
      image: '/assets/experience-guide-1.png',
      title: t('properties/iguazu:experiences.activities.guarani.title'),
      location: t('properties/iguazu:experiences.activities.guarani.location'),
      description: t('properties/iguazu:experiences.activities.guarani.description')
    },
    {
      id: 2,
      image: '/assets/experience-guide-2.png',
      title: t('properties/iguazu:experiences.activities.yacui.title'),
      location: t('properties/iguazu:experiences.activities.yacui.location'),
      description: t('properties/iguazu:experiences.activities.yacui.description')
    },
    {
      id: 3,
      image: '/assets/experience-guide-3.png',
      title: t('properties/iguazu:experiences.activities.jesuit.title'),
      location: t('properties/iguazu:experiences.activities.jesuit.location'),
      description: t('properties/iguazu:experiences.activities.jesuit.description')
    },
    {
      id: 4,
      image: '/assets/experience-guide-4.png',
      title: t('properties/iguazu:experiences.activities.guiraOga.title'),
      location: t('properties/iguazu:experiences.activities.guiraOga.location'),
      description: t('properties/iguazu:experiences.activities.guiraOga.description')
    },
    {
      id: 5,
      image: '/assets/experience-guide-5.png',
      title: t('properties/iguazu:experiences.activities.boatRide.title'),
      location: t('properties/iguazu:experiences.activities.boatRide.location'),
      description: t('properties/iguazu:experiences.activities.boatRide.description')
    },
    {
      id: 6,
      image: '/assets/experience-guide-6.png',
      title: t('properties/iguazu:experiences.activities.devilsThroat.title'),
      location: t('properties/iguazu:experiences.activities.devilsThroat.location'),
      description: t('properties/iguazu:experiences.activities.devilsThroat.description')
    }
  ];

  // Team members data with translated content
  const teamMembers = [
    {
      id: 1,
      image: '/assets/images/localhost/9fe76396d22e63fa2f0e5f856d1c5e01988dbbb6.png',
      name: t('properties/iguazu:experiences.team.members.carlosBarros.name'),
      role: t('properties/iguazu:experiences.team.members.carlosBarros.role'),
      description: t('properties/iguazu:experiences.team.members.carlosBarros.description')
    },
    {
      id: 2,
      image: '/assets/images/localhost/b23b62a9f96cc5ab8a93da33fe544df6c49884a3.png',
      name: t('properties/iguazu:experiences.team.members.cristianLopez.name'),
      role: t('properties/iguazu:experiences.team.members.cristianLopez.role'),
      description: t('properties/iguazu:experiences.team.members.cristianLopez.description')
    },
    {
      id: 3,
      image: '/assets/images/localhost/0900d5204eb6e5804f3a3f031befd03ac9cff02f.png',
      name: t('properties/iguazu:experiences.team.members.paulaBertotto.name'),
      role: t('properties/iguazu:experiences.team.members.paulaBertotto.role'),
      description: t('properties/iguazu:experiences.team.members.paulaBertotto.description')
    }
  ];

  return (
    <div className="experiences-iguazu">
      {/* Navigation - Iguazu variant (light) */}
      <UnifiedNavigation variant="light" destination="iguazu" />

      {/* Text Block Header */}
      <section className="experiences-header-section" data-scroll-section>
        <div className="experiences-header-content">
          <h1>{t('properties/iguazu:experiences.pageTitle')}</h1>
          <p>
            {t('properties/iguazu:experiences.pageDescription')}
          </p>
        </div>
      </section>

      {/* Full Width Hero Image */}
      <section className="experiences-hero-image" data-scroll-section>
        <div 
          className="hero-image-container"
          style={{ backgroundImage: `url('/assets/experiencepagebreak.jpg')` }}
        />
      </section>

      {/* Guide/Experiences Section - 3x2 Grid */}
      <section className="experiences-guide-section" data-scroll-section>
        <div className="guide-section-container">
          {/* Filters and Guide Button */}
          <div className="guide-section-header">
            <div className="filter-buttons">
              <button className="filter-button active">
                <span>{t('properties/iguazu:experiences.filters.location')}</span>
              </button>
              <button className="filter-button">
                <span>{t('properties/iguazu:experiences.filters.type')}</span>
              </button>
            </div>
            <button className="guide-button">
              <span>{t('properties/iguazu:experiences.filters.ourGuides')}</span>
            </button>
          </div>

          <div className="experiences-grid">
            {experiences.map((experience) => (
              <div key={experience.id} className="experience-card">
                <div className="experience-image">
                  <img src={experience.image} alt={experience.title} />
                </div>
                <div className="experience-content">
                  <h3>{experience.title}</h3>
                  <span className="experience-location">{experience.location}</span>
                  <p>{experience.description}</p>
                  <button className="contact-link">
                    {t('properties/iguazu:experiences.contactUs')}
                    <svg className="underline" viewBox="0 0 78 2" fill="none">
                      <line y1="1" x2="78" y2="1" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="guide-pagination">
            <button className="pagination-button active">{t('properties/iguazu:experiences.pagination.page1')}</button>
            <button className="pagination-button">{t('properties/iguazu:experiences.pagination.page2')}</button>
            <button className="pagination-button">{t('properties/iguazu:experiences.pagination.page3')}</button>
            <button className="pagination-button">{t('properties/iguazu:experiences.pagination.page4')}</button>
          </div>
        </div>
      </section>

      {/* Team Header + Body */}
      <section className="experiences-team-section" data-scroll-section>
        <div className="team-section-container">
          <div className="team-header">
            <h2>{t('properties/iguazu:experiences.team.sectionTitle')}</h2>
            <p>
              {t('properties/iguazu:experiences.team.sectionDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Team Carousel */}
      <section className="experiences-team-carousel" data-scroll-section>
        <div className="team-carousel-wrapper">
          <div className="team-carousel-container" id="team-carousel">
            <div className="team-members-scroll">
              {teamMembers.map((member) => (
                <div key={member.id} className="team-member-card">
                  <div className="member-image">
                    {member.image ? (
                      <img src={member.image} alt={member.name} />
                    ) : (
                      <div className="placeholder-image" />
                    )}
                  </div>
                  <div className="member-content">
                    <h3>{member.name}</h3>
                    <span className="member-role">{member.role}</span>
                    <p>{member.description}</p>
                    <button className="read-more-link">
                      {t('properties/iguazu:experiences.team.readMore')}
                      <svg className="underline" viewBox="0 0 76 2" fill="none">
                        <line y1="1" x2="76" y2="1" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <button className="carousel-nav-arrow" aria-label="Next">
              <svg width="58" height="58" viewBox="0 0 58 58" fill="none">
                <path d="M22 14L37 29L22 44" stroke="#49413d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </section>

      {/* Call to Action Full Image */}
      <section className="experiences-cta-section" data-scroll-section>
        <div 
          className="cta-image-container"
          style={{ backgroundImage: `url('/assets/fullviewportatacama.jpg')` }}
        />
      </section>

      {/* Instagram Gallery */}
      <div className="instagram-section-wrapper">
        <InstagramSection />
      </div>

      {/* Footer - Iguazu variant */}
      <Footer variant="iguazu" />
    </div>
  );
};

export default ExperiencesIguazu;